import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const lines = [
  { text: "İşletmenizin", accent: false },
  { text: "Dijital Potansiyelini", accent: true },
  { text: "Ücretsiz Keşfedin", accent: false },
];

const stats = ["500+ KOBİ Analizi", "%35 Verimlilik Artışı", "24 Saatte Dönüş"];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.2 + i * 0.13, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 700], [0, 90]);
  const bgY = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-24 overflow-hidden hero-grid-bg" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0000FF]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="inline-flex items-center gap-2.5 rounded-full border border-[#0D9488]/30 bg-[#0D9488]/5 px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0D9488] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0D9488]" />
            </span>
            <span className="text-xs sm:text-sm font-semibold text-[#0D9488] tracking-wide">%100 Ücretsiz KOBİ Dijital Sağlık Taraması</span>
          </motion.div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" data-testid="hero-title">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className={`block ${line.accent ? "text-[#0000FF]" : "text-[#0F172A]"}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8, ease: "easeOut" }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-xl"
            data-testid="hero-subtitle"
          >
            Kobi-Line uzmanları firmanızın dijital olgunluğunu analiz etsin, size özel
            büyüme yol haritanızı çıkarsın. Formu doldurun, 24 saat içinde raporunuzla dönelim.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#form"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0000FF] text-white font-semibold px-7 py-3.5 shadow-xl shadow-[#0000FF]/25 transition-all hover:bg-[#0000D6] hover:shadow-[#0000FF]/40 active:scale-[0.98]"
            >
              <Sparkles size={18} />
              Ücretsiz Analizi Başlat
            </a>
            <div className="flex flex-wrap gap-2" data-testid="hero-stats">
              {stats.map((s) => (
                <span key={s} className="rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-xs sm:text-sm font-medium text-[#475569]">
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[#0000FF]/10 rounded-[2.5rem] blur-2xl" />
            <motion.div style={{ y: imageY }} className="relative rounded-[2rem] overflow-hidden border border-[#0F172A]/10 shadow-2xl shadow-[#0F172A]/15">
              <img
                src="https://images.unsplash.com/photo-1758691736407-02406d18df6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwdHJhbnNmb3JtYXRpb24lMjBkYXRhJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwYnVzaW5lc3MlMjBtZWV0aW5nfGVufDB8fHx8MTc4OTExMDMzNnww&ixlib=rb-4.1.0&q=85"
                alt="Kobi-Line dijital dönüşüm analizi"
                className="w-full h-[320px] sm:h-[420px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#93C5FD]">Kobi-Line Analiz Motoru</p>
                  <p className="font-display text-white font-bold text-lg mt-1">Veriye Dayalı Büyüme</p>
                </div>
                <span className="rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-semibold px-3 py-1.5">Canlı Rapor</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#surec"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-[#94A3B8] hover:text-[#0000FF] transition-colors"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Keşfet</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
